import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { Reveal } from "./Reveal";

const MOSAIC = [
  { role: "Senior Data Engineer", img: "/assets/mosaic/engineer-data.jpg" },
  { role: "Analytics Engineer", img: "/assets/mosaic/engineer-analytics.jpg" },
  { role: "Cloud Architect", img: "/assets/mosaic/engineer-cloud.jpg" },
  { role: "MLOps Specialist", img: "/assets/mosaic/engineer-mlops.jpg" },
];

type HeroCoreProps = { onQuote: () => void };

export default function HeroCore({ onQuote }: HeroCoreProps) {
  // Render each hero image only on its target viewport so mobile never downloads the
  // 4 desktop portraits and desktop never downloads the mobile composite.
  const [isDesktop, setIsDesktop] = useState(
    () => typeof window !== "undefined" && window.matchMedia("(min-width: 1024px)").matches
  );
  useEffect(() => {
    const mq = window.matchMedia("(min-width: 1024px)");
    const onChange = (e: MediaQueryListEvent) => setIsDesktop(e.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);
  return (
    <section className="relative overflow-hidden bg-[#03383F] text-white" data-testid="hero-section">
      <div className="mx-auto grid max-w-6xl items-center px-5 pb-14 sm:px-8 lg:grid-cols-12 lg:gap-12 lg:pb-24 lg:pt-32">
        <div className="relative z-10 -mt-48 space-y-3 lg:col-span-7 lg:mt-0 lg:space-y-7">
          <Reveal delay={0.1}>
            <p className="font-cond text-xs font-bold uppercase tracking-[0.28em] text-brand-orange">
              Staff Augmentation · Senior Data &amp; Cloud
            </p>
          </Reveal>
          <Reveal delay={0.18}>
            <h1
              className="font-display text-4xl font-extrabold leading-[1.08] tracking-tight sm:text-5xl lg:text-6xl"
              data-testid="hero-headline"
            >
              Nearshore talent,
              <br />
              at the{" "}
              <span className="relative inline-block text-brand-orange">
                core
                <svg
                  className="absolute -bottom-1 left-0 h-2 w-full text-brand-orange/60"
                  viewBox="0 0 100 12"
                  preserveAspectRatio="none"
                >
                  <path d="M0 8 Q 50 12, 100 4" stroke="currentColor" strokeWidth="4" fill="none" />
                </svg>
              </span>{" "}
              of your team.
            </h1>
          </Reveal>
          <Reveal delay={0.26}>
            <p className="max-w-xl text-base leading-snug text-slate-300 sm:text-lg lg:leading-relaxed">
              <span className="font-semibold text-white">nearcore</span> places senior Data &amp; Cloud
              specialists from Latin America directly into US teams, working in your time zone. No
              resellers, no recruiting fees. Just vetted talent, transparent rates and long-term
              continuity.
            </p>
          </Reveal>
          <Reveal delay={0.34}>
            <div className="flex flex-wrap items-center gap-4">
              <motion.button
                type="button"
                onClick={onQuote}
                data-testid="hero-cta-primary"
                whileHover={{ scale: 1.04 }}
                whileTap={{ scale: 0.97 }}
                className="inline-flex items-center gap-2 rounded-full bg-brand-orange px-6 py-3 text-sm font-bold text-white shadow-lg shadow-brand-orange/25 transition-colors duration-300 hover:bg-brand-orange-dark lg:px-7 lg:py-3.5 lg:text-base"
              >
                Augment your team <ArrowRight size={17} />
              </motion.button>
              <div
                className="inline-flex items-center gap-2 rounded-full border border-[#008390]/30 bg-[#00555E]/50 px-5 py-3 text-xs font-semibold text-amber-300 backdrop-blur-md lg:text-sm"
                data-testid="hero-rating-badge"
              >
                4.9/5 · rated by US clients
              </div>
            </div>
          </Reveal>
        </div>

        {/* Mosaic */}
        <div className="relative -order-1 -mx-5 sm:-mx-8 lg:order-none lg:col-span-5 lg:mx-0" data-testid="hero-mosaic-container">
          {/* Mobile: single composite image with baked fade */}
          {!isDesktop && (
            <img
              src="/assets/mosaic/hero-mobile.webp"
              alt="Senior nearcore Data and Cloud engineers"
              className="w-full h-auto"
              width="941"
              height="1314"
              fetchPriority="high"
              data-testid="hero-mobile-image"
            />
          )}
          <div className="pointer-events-none absolute inset-y-0 left-0 z-10 w-10 bg-gradient-to-r from-[#03383F] to-transparent lg:hidden" />
          <div className="pointer-events-none absolute inset-y-0 right-0 z-10 w-10 bg-gradient-to-l from-[#03383F] to-transparent lg:hidden" />
          {/* Desktop: 2x2 grid */}
          {isDesktop && (
          <Reveal delay={0.25}>
            <div className="relative grid grid-cols-2 gap-4 rounded-2xl border border-[#00555E]/40 bg-[#01252A]/60 p-4 shadow-2xl backdrop-blur-xl">
              {MOSAIC.map((card, i) => (
                <motion.div
                  key={card.role}
                  whileHover={{ scale: 1.02, y: -3 }}
                  transition={{ duration: 0.3 }}
                  className="group relative overflow-hidden border border-transparent transition-colors duration-300 hover:border-brand-orange lg:rounded-xl"
                  data-testid={`hero-mosaic-card-${i}`}
                >
                  <img
                    src={card.img}
                    alt={`${card.role} based in Latin America`}
                    className="aspect-[5/4] w-full object-cover object-[center_20%] lg:aspect-square"
                    width="500"
                    height="392"
                    loading={i > 1 ? "lazy" : undefined}
                  />
                  <div className="absolute inset-x-0 bottom-0 hidden bg-gradient-to-t from-[#01252A] via-[#01252A]/70 to-transparent px-3 pb-2.5 pt-8 lg:block">
                    <p className="font-display text-[13px] font-bold leading-tight text-white">
                      {card.role}
                    </p>
                    <p className="mt-0.5 font-cond text-[10px] font-bold uppercase tracking-[0.2em] text-brand-orange">
                      7+ yrs · Your hours
                    </p>
                  </div>
                </motion.div>
              ))}
            </div>
          </Reveal>
          )}
        </div>
      </div>
    </section>
  );
}
